/* Surisa — motor de unidades compartido (toggle Métrico ↔ Imperial).
 *
 *  Extraído de `catalog-table.js` (v0.15.0) para que lo consuman tanto el
 *  catálogo Nivel 2 (`<surisa-catalog-table>`) como la tabla de
 *  especificaciones del PDP (`<surisa-spec-table>`) sin duplicar la lógica
 *  ni arriesgar que diverjan.
 *
 *  Patrón del theme: IIFE + `window.SurisaUnits` (sin bundler, sin modules).
 *  Cargar SIEMPRE antes que cualquier consumidor (`catalog-table.js`,
 *  `surisa-spec-table.js`) — ambos lo leen en su IIFE de arranque.
 *
 *  Convención de claves (CRÍTICA): la `key` de una columna/fila SIEMPRE
 *  lleva el sufijo MÉTRICO canónico (`_mm`, `_n`, `_n_m`, `_g`). El cell key
 *  real se compone según el sistema activo:
 *    - sistema métrico  (unit === 'mm'): cell key === col.key
 *    - sistema imperial (unit === 'in'): swap del sufijo vía SUFFIX_PAIRS
 *      (longest-first: `_n_m` antes que `_n`).
 *  El string `unit` sigue siendo 'mm'/'in' por compat con los URL params
 *  (?unit=mm/in) — semánticamente representa el SISTEMA, no la unidad. */

(function () {
  'use strict';

  if (window.SurisaUnits) return; // idempotente — no redefinir si ya existe

  /** Pares de sufijos métrico→imperial. ORDEN IMPORTA (longest-first):
   *  `_n_m`/`_ft_lbs` debe ir ANTES que `_n`/`_lb` para que un key como
   *  `torque_n_m` no haga match parcial con `_n`. */
  var SUFFIX_PAIRS = [
    ['_n_m', '_ft_lbs'],   // ANTES que '_n'
    ['_mm',  '_in'],
    ['_n',   '_lb'],
    ['_g',   '_oz']        // futuro, ya cableado (sin columna de peso aún)
  ];

  /** unit_bases que participan en el toggle. Los `unitless` (material,
   *  erp_code, bolt_size_imperial…) pasan a través sin swap. */
  var TOGGLE_UNIT_BASES = { mm: true, n: true, n_m: true, g: true };

  /** Literales de unidad por unit_base × sistema. Los consume el render del
   *  header (`[mm]`, `[in]`, `[N]`, `[lb]`…). */
  var UNIT_DISPLAY = {
    metric:   { mm: 'mm', n: 'N',  n_m: 'N·m',    g: 'g'  },
    imperial: { mm: 'in', n: 'lb', n_m: 'ft·lbs', g: 'oz' }
  };

  /** Devuelve el cell key a leer de row.cells según unit/sistema activo.
   *  Idéntico al original de catalog-table.js (movido 1:1). */
  function resolveCellKey(col, unit) {
    if (unit === 'mm') return col.key;
    if (!col.unit_base || !TOGGLE_UNIT_BASES[col.unit_base]) return col.key;
    for (var i = 0; i < SUFFIX_PAIRS.length; i++) {
      var suf = SUFFIX_PAIRS[i][0];
      if (col.key.length > suf.length && col.key.slice(-suf.length) === suf) {
        return col.key.slice(0, -suf.length) + SUFFIX_PAIRS[i][1];
      }
    }
    return col.key;
  }

  /** Parsea un valor imperial fraccionario a decimal para sort/compare.
   *  "1-3/8\"" → 1.375, "1-1/2\"" → 1.5, "2\"" → 2. NaN para otras formas.
   *  Idéntico al original de catalog-table.js (movido 1:1). */
  function parseImperialFractional(s) {
    if (s == null || s === '') return NaN;
    var str = String(s).replace(/"/g, '').trim();
    var m = str.match(/^(\d+)(?:-(\d+)\/(\d+))?$/);
    if (!m) return NaN;
    var whole = Number(m[1]);
    if (m[2] && m[3]) {
      var num = Number(m[2]);
      var den = Number(m[3]);
      if (!isFinite(den) || den === 0) return NaN;
      return whole + (num / den);
    }
    return whole;
  }

  /** Literal de unidad a mostrar para un unit_base, dado el sistema y un
   *  override opcional (`col.unit_display`). Devuelve '' si la columna no
   *  participa en el toggle y no hay override. Conveniencia para los headers
   *  de la spec table (el catálogo tiene su propio applyUnitUI inline).
   *  El override aplica SIEMPRE en métrico; en imperial solo si la columna NO
   *  participa en el toggle (unitless / base fuera de TOGGLE_UNIT_BASES) — en
   *  ese caso valor y unidad son invariantes al sistema (ej. "N/mm" de la
   *  rigidez o "mm" de una sección textual), así que el override debe persistir.
   *  Para bases que togglean, imperial usa UNIT_DISPLAY[imperial]. (El catálogo
   *  no sufría esto: su applyUnitUI ignora las columnas unitless y conserva el
   *  unit_display del SSR en ambos sistemas.) */
  function unitDisplayLabel(unitBase, unit, override) {
    var system = unit === 'in' ? 'imperial' : 'metric';
    if (override && (system === 'metric' || !TOGGLE_UNIT_BASES[unitBase])) return override;
    var table = UNIT_DISPLAY[system] || {};
    return table[unitBase] || '';
  }

  /** Formatea un valor crudo de celda para render, aplicando decimales
   *  (decimals_mm/decimals_in del col) + value_prefix/value_suffix.
   *  Devuelve null si el valor es vacío/null (el consumidor pinta el guion).
   *
   *  Espejo de la rama `default` de fillCell() en catalog-table.js — lo
   *  comparten catálogo y spec table para que no diverjan. `numeric_parser`
   *  'imperial_fractional' se renderiza tal cual (string libre). */
  function formatValue(col, raw, unit) {
    if (raw == null || raw === '') return null;
    var decimals = (unit === 'in' && col.decimals_in != null)
      ? col.decimals_in
      : (col.decimals_mm != null ? col.decimals_mm : 1);
    var prefix = col.value_prefix || '';
    var suffix = col.value_suffix || '';
    var num = Number(raw);
    var body = isNaN(num) ? String(raw) : num.toFixed(decimals);
    return prefix + body + suffix;
  }

  window.SurisaUnits = {
    SUFFIX_PAIRS: SUFFIX_PAIRS,
    TOGGLE_UNIT_BASES: TOGGLE_UNIT_BASES,
    UNIT_DISPLAY: UNIT_DISPLAY,
    resolveCellKey: resolveCellKey,
    parseImperialFractional: parseImperialFractional,
    unitDisplayLabel: unitDisplayLabel,
    formatValue: formatValue
  };
})();
